import { Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { SortColumn } from 'src/app/models/sortColumn.model';
import { StoreService } from 'src/app/services/infra/store/store.service';
import { ISelectOption } from '../../../selectOptions';
import { FilterTypesEnum } from '../../filterTypes.enum';
import { IPageFilters } from '../../interfaces/pageFilters.interface';
import { ISelectedFilters } from '../../selectedFilters.interface';
import { DateFilter } from '../filtersTypes/dateFilter.model';
import { DynamicMultiSelectFilters } from '../filtersTypes/dynamicFilter.model';
import { MultiSelectFilters } from '../filtersTypes/multiFilter.model';
import { FeatureFlagsService } from 'src/app/services/infra/featureFlags/feature-flags.service';
import { AiAgentStepsHttpService } from 'src/app/services/httpServices/detection/aiAgentStepsHttpService/ai-agent-steps-http.service';
import { FiltersQueryTransformer } from 'src/app/services/filters/filtersQueryTransformer.ts/filtersQueryTransformer';
import { CreationTimeSinceOptions } from 'src/app/models/selectOptions/creationTimeSinceOptions';
import moment from 'moment';
import {
  StepTypeSelectOptions,
  getStepTypeSelectOptions,
} from 'src/app/models/selectOptions/stepTypeSelectOptions.model';
import { checkAllGivenIdsExistInOptions, fromIntegrationToSelectOption } from './utils';
import { IntegrationModel } from 'src/app/models/api';
import { ChatModeSelectOptions } from 'src/app/models/selectOptions/chatModeSelectOptions.mode';
import { AutocompleteSearchResultsV2Model } from 'src/app/models/api/autocompleteSearchResultsV2.model';
import { DisplayCamelCaseStringPipe } from 'src/app/pipes/display-camel-case-string/display-camel-case-string.pipe';
import { agentStepsActivityTableColumns } from 'src/app/models/tableColumns/aiAgentStepActivityTableColumn';
import { ITableColumn } from 'src/app/models/tableColumns';
import { getActiveFindingAiServicesFromIntegrations } from 'src/app/common/integrationToFilterUtils';
import {
  getAiServiceLogoPath,
  getAiServiceDisplayName,
  getClientApplicationIconPath,
  getClientApplicationDisplayName,
} from 'src/app/common/aiServiceUtils';
import { AgentClientApplication } from 'src/app/models/api/agentStep.model';
import { PermissionsService } from 'src/app/services/infra/permissionsService/permissions.service';
import { PERMISSIONS } from 'src/app/models/permissions/permissions.const';
import { ToggleOption } from 'src/app/components/option-toggle/option-toggle.component';
import { FeatureFlagTypes } from 'src/app/models/featureFlag/featureFlagsTypes.enum';
import {
  aidrClientApplicationSelectOptions,
  inlineUnifiedClientApplicationSelectOptions,
} from 'src/app/models/selectOptions/unifiedClientApplicationSelectOptions.model';
import {
  aidrAiServiceSelectOptions,
  aidrAiServiceSelectOptionsWithRsaDemo,
  inlineUnifiedAiServiceSelectOptions,
} from 'src/app/models/selectOptions/unifiedAiServiceSelectOptions.model';

export class AiActivityFiltersModel implements IPageFilters {
  public onUpdate$: BehaviorSubject<ISelectedFilters>;
  public allOptions$: BehaviorSubject<ISelectOption[]> = new BehaviorSubject<ISelectOption[]>([]);
  public typeFilters: MultiSelectFilters;
  public fromDate: DateFilter;
  public toDate: DateFilter;
  public actorFilters: DynamicMultiSelectFilters;
  public threadIdFilters: DynamicMultiSelectFilters;
  public agentFilters: DynamicMultiSelectFilters;
  public environmentFilters: DynamicMultiSelectFilters;
  public creationTimeSinceDate: MultiSelectFilters;
  public integrationFilters: MultiSelectFilters;
  public aiServiceFilters: MultiSelectFilters;

  public clientApplicationFilters: MultiSelectFilters;
  public chatModeFilters: MultiSelectFilters;
  public toolOriginFilters: DynamicMultiSelectFilters;

  public readonly defaultSortColumn: ITableColumn | undefined = agentStepsActivityTableColumns.find(
    (column) => column.sortKey === 'timestamp'
  );

  public dataItemFilters: DynamicMultiSelectFilters;

  public sortedColumn$: BehaviorSubject<SortColumn | undefined> = new BehaviorSubject<SortColumn | undefined>(
    this.defaultSortColumn ? { column: this.defaultSortColumn, isDesc: true } : undefined
  );

  private _currentToggleMode: ToggleOption = 'option1';
  private _isUnifiedModeActive = false;

  constructor(
    private readonly _router: Router,
    private readonly _store: StoreService,
    private readonly _aiActivityHttpService: AiAgentStepsHttpService,
    public featureFlagsService: FeatureFlagsService,
    private readonly _displayCamelCaseStringPipe: DisplayCamelCaseStringPipe,
    private readonly _permissionsService: PermissionsService
  ) {
    this._initializeFilters();
    this._integrationsOnUpdate();
    this._updateDynamicFiltersFromIntegrations();

    this.onUpdate$ = new BehaviorSubject(this.getSelectedFilters());
  }

  public getSelectedFilters(): ISelectedFilters {
    return {
      fromDate: this.fromDate.getFilterValue(),
      toDate: this.toDate.getFilterValue(),
      type: this.typeFilters.getSelectedFiltersValues(),
      actor: this.actorFilters.getSelectedFiltersValues(),
      threadId: this.threadIdFilters.getSelectedFiltersValues(),
      integration: this.integrationFilters.getSelectedFiltersValues(),
      aiService: this.aiServiceFilters.getSelectedFiltersValues(),
      clientApplication: this.clientApplicationFilters.getSelectedFiltersValues(),
      agent: this.agentFilters.getSelectedFiltersValues(),
      environment: this.environmentFilters.getSelectedFiltersValues(),
      chatMode: this.chatModeFilters.getSelectedFiltersValues(),
      toolOrigin: this.toolOriginFilters.getSelectedFiltersValues(),
      dataItem: this.dataItemFilters.getSelectedFiltersValues(),
    };
  }

  public getAllFiltersOptions(): BehaviorSubject<ISelectOption[]> {
    return this.allOptions$;
  }

  public getAllSelectedOptions(): ISelectOption[] {
    const selectedOptions = [
      ...this.creationTimeSinceDate.getSelectedOptions(),
      ...this.typeFilters.getSelectedOptions(),
      ...this.actorFilters.getSelectedOptions(),
      ...this.threadIdFilters.getSelectedOptions(),
      ...this.integrationFilters.getSelectedOptions(),
      ...this.aiServiceFilters.getSelectedOptions(),
      ...this.clientApplicationFilters.getSelectedOptions(),
      ...this.agentFilters.getSelectedOptions(),
      ...this.environmentFilters.getSelectedOptions(),
      ...this.chatModeFilters.getSelectedOptions(),
      ...this.toolOriginFilters.getSelectedOptions(),
      ...this.dataItemFilters.getSelectedOptions(),
    ];

    return selectedOptions;
  }

  public updateFilterFromUserSelection(
    filterToUpdate: FilterTypesEnum | undefined,
    value: ISelectOption[] | Date | boolean | string | undefined
  ) {
    let needUpdate = false;
    if (filterToUpdate) {
      switch (filterToUpdate) {
        case FilterTypesEnum.FromDate:
          this.fromDate.update(value as Date | undefined);
          needUpdate = true;
          break;
        case FilterTypesEnum.ToDate:
          this.toDate.update(value as Date | undefined);
          needUpdate = true;
          break;
      }
    } else {
      if (value instanceof Array) {
        if (value.length == 0) {
          needUpdate = this.clearAllMultiSelectFiltersIfNeeded();
        } else {
          const optionsByCategory: {
            [key: string]: ISelectOption[];
          } = {};
          value.forEach((option) => {
            if (option.filterType) {
              if (option.filterType in optionsByCategory) {
                optionsByCategory[option.filterType].push(option);
              } else {
                optionsByCategory[option.filterType] = [option];
              }
            }
          });
          this._updateAllOptions();

          needUpdate = this.typeFilters.updateIfNeeded(optionsByCategory[FilterTypesEnum.StepType] || []) || needUpdate;
          needUpdate = this.actorFilters.updateIfNeeded(optionsByCategory[FilterTypesEnum.Actor] || []) || needUpdate;
          needUpdate =
            this.threadIdFilters.updateIfNeeded(optionsByCategory[FilterTypesEnum.ThreadId] || []) || needUpdate;
          needUpdate =
            this.integrationFilters.updateIfNeeded(optionsByCategory[FilterTypesEnum.Integration] || []) || needUpdate;
          needUpdate =
            this.aiServiceFilters.updateIfNeeded(optionsByCategory[FilterTypesEnum.AiService] || []) || needUpdate;
          needUpdate =
            this.clientApplicationFilters.updateIfNeeded(optionsByCategory[FilterTypesEnum.ClientApplication] || []) ||
            needUpdate;
          needUpdate =
            this.agentFilters.updateIfNeeded(optionsByCategory[FilterTypesEnum.AIAgentName] || []) || needUpdate;
          needUpdate =
            this.environmentFilters.updateIfNeeded(optionsByCategory[FilterTypesEnum.Environment] || []) || needUpdate;
          needUpdate =
            this.chatModeFilters.updateIfNeeded(optionsByCategory[FilterTypesEnum.ChatMode] || []) || needUpdate;
          needUpdate =
            this.toolOriginFilters.updateIfNeeded(optionsByCategory[FilterTypesEnum.ToolOrigin] || []) || needUpdate;
        }
      }
    }
    if (needUpdate) {
      const filtersQuery = FiltersQueryTransformer.transformToQuery(this.getSelectedFilters());
      this._router.navigate([], {
        queryParams: {
          q: filtersQuery,
        },
        queryParamsHandling: 'merge',
      });
      this.onUpdate$.next(this.getSelectedFilters());
    }
  }

  public getFilterOptionsIfExist$(filter: FilterTypesEnum): BehaviorSubject<ISelectOption[]> {
    switch (filter) {
      case FilterTypesEnum.StepType:
        return this.typeFilters.getOptions$();
      case FilterTypesEnum.Actor:
        return this.actorFilters.getOptions$();
      case FilterTypesEnum.ThreadId:
        return this.threadIdFilters.getOptions$();
      case FilterTypesEnum.Integration:
        return this.integrationFilters.getOptions$();
      case FilterTypesEnum.AiService:
        return this.aiServiceFilters.getOptions$();
      case FilterTypesEnum.ClientApplication:
        return this.clientApplicationFilters.getOptions$();
      case FilterTypesEnum.AIAgentName:
        return this.agentFilters.getOptions$();
      case FilterTypesEnum.Environment:
        return this.environmentFilters.getOptions$();
      case FilterTypesEnum.ChatMode:
        return this.chatModeFilters.getOptions$();
      case FilterTypesEnum.ToolOrigin:
        return this.toolOriginFilters.getOptions$();
      default:
        throw new Error(`AiActivityFiltersModel getFilterOptionsIfExist: ${filter} not supported`);
    }
  }

  public removeSelectedOptionsIfExist(options: ISelectOption[]): void {
    const optionsIdsByCategory: { [key: string]: Set<string> } = {};
    options.forEach(
      (option) => {
        if (option.filterType) {
          if (option.filterType in optionsIdsByCategory) {
            optionsIdsByCategory[option.filterType].add(option.id);
          } else {
            optionsIdsByCategory[option.filterType] = new Set([option.id]);
          }
        }
      },
      this.getDynamicFilters().forEach((filter) => {
        optionsIdsByCategory[filter] = new Set();
      })
    );

    if (optionsIdsByCategory[FilterTypesEnum.StepType]) {
      this.typeFilters.updateIfNeeded(
        this.typeFilters
          .getSelectedOptions()
          .filter((selectedOption) => !optionsIdsByCategory[FilterTypesEnum.StepType].has(selectedOption.id))
      );
    }

    if (optionsIdsByCategory[FilterTypesEnum.Actor]) {
      this.actorFilters.updateIfNeeded(
        this.actorFilters
          .getSelectedOptions()
          .filter((selectedOption) => !optionsIdsByCategory[FilterTypesEnum.Actor].has(selectedOption.id))
      );
    }
    if (optionsIdsByCategory[FilterTypesEnum.ThreadId]) {
      this.threadIdFilters.updateIfNeeded(
        this.threadIdFilters
          .getSelectedOptions()
          .filter((selectedOption) => !optionsIdsByCategory[FilterTypesEnum.ThreadId].has(selectedOption.id))
      );
    }
    if (optionsIdsByCategory[FilterTypesEnum.Integration]) {
      this.integrationFilters.updateIfNeeded(
        this.integrationFilters
          .getSelectedOptions()
          .filter((selectedOption) => !optionsIdsByCategory[FilterTypesEnum.Integration].has(selectedOption.id))
      );
    }
    if (optionsIdsByCategory[FilterTypesEnum.AiService]) {
      this.aiServiceFilters.updateIfNeeded(
        this.aiServiceFilters
          .getSelectedOptions()
          .filter((selectedOption) => !optionsIdsByCategory[FilterTypesEnum.AiService].has(selectedOption.id))
      );
    }
    if (optionsIdsByCategory[FilterTypesEnum.ClientApplication]) {
      this.clientApplicationFilters.updateIfNeeded(
        this.clientApplicationFilters
          .getSelectedOptions()
          .filter((selectedOption) => !optionsIdsByCategory[FilterTypesEnum.ClientApplication].has(selectedOption.id))
      );
    }

    if (optionsIdsByCategory[FilterTypesEnum.AIAgentName]) {
      this.agentFilters.updateIfNeeded(
        this.agentFilters
          .getSelectedOptions()
          .filter((selectedOption) => !optionsIdsByCategory[FilterTypesEnum.AIAgentName].has(selectedOption.id))
      );
    }

    if (optionsIdsByCategory[FilterTypesEnum.Environment]) {
      this.environmentFilters.updateIfNeeded(
        this.environmentFilters
          .getSelectedOptions()
          .filter((selectedOption) => !optionsIdsByCategory[FilterTypesEnum.Environment].has(selectedOption.id))
      );
    }
    if (optionsIdsByCategory[FilterTypesEnum.ChatMode]) {
      this.chatModeFilters.updateIfNeeded(
        this.chatModeFilters
          .getSelectedOptions()
          .filter((selectedOption) => !optionsIdsByCategory[FilterTypesEnum.ChatMode].has(selectedOption.id))
      );
    }

    if (optionsIdsByCategory[FilterTypesEnum.ToolOrigin]) {
      this.toolOriginFilters.updateIfNeeded(
        this.toolOriginFilters
          .getSelectedOptions()
          .filter((selectedOption) => !optionsIdsByCategory[FilterTypesEnum.ToolOrigin].has(selectedOption.id))
      );
    }
    const filtersQuery = FiltersQueryTransformer.transformToQuery(this.getSelectedFilters());
    this._router.navigate([], {
      queryParams: { q: filtersQuery },
      queryParamsHandling: 'merge',
    });
    this.onUpdate$.next(this.getSelectedFilters());
  }

  public getFilterSelectedIfExist(filter: FilterTypesEnum): string[] {
    switch (filter) {
      case FilterTypesEnum.StepType:
        return this.typeFilters.getSelectedFiltersIds();
      case FilterTypesEnum.Actor:
        return this.actorFilters.getSelectedFiltersIds();
      case FilterTypesEnum.ThreadId:
        return this.threadIdFilters.getSelectedFiltersIds();
      case FilterTypesEnum.Integration:
        return this.integrationFilters.getSelectedFiltersIds();
      case FilterTypesEnum.AiService:
        return this.aiServiceFilters.getSelectedFiltersIds();
      case FilterTypesEnum.ClientApplication:
        return this.clientApplicationFilters.getSelectedFiltersIds();
      case FilterTypesEnum.AIAgentName:
        return this.agentFilters.getSelectedFiltersIds();
      case FilterTypesEnum.Environment:
        return this.environmentFilters.getSelectedFiltersIds();
      case FilterTypesEnum.ChatMode:
        return this.chatModeFilters.getSelectedFiltersIds();
      case FilterTypesEnum.ToolOrigin:
        return this.toolOriginFilters.getSelectedFiltersIds();
      default:
        throw new Error(`AiActivityFiltersModel getFilterSelectedIfExist: ${filter} not supported`);
    }
  }

  public updateFiltersByKeyValue(keyValueFilters: { [key: string]: string[] }, updateQueryParams = false): void {
    let needUpdate = false;

    let values: string[] = [];

    if ('fromDate' in keyValueFilters || this.fromDate.getFilterValue() !== undefined) {
      const fromDateMoment =
        keyValueFilters['fromDate'] && moment(keyValueFilters['fromDate'][0], 'x').isValid()
          ? moment(keyValueFilters['fromDate'][0], 'x')
          : null;

      // Reject future dates - set to undefined if date is in the future
      const fromDate = fromDateMoment && !fromDateMoment.isAfter(moment()) ? fromDateMoment.toDate() : undefined;

      this.fromDate.update(fromDate);
      needUpdate = true;
    }

    if ('toDate' in keyValueFilters || this.toDate.getFilterValue() !== undefined) {
      const toDateMoment =
        keyValueFilters['toDate'] && moment(keyValueFilters['toDate'][0], 'x').isValid()
          ? moment(keyValueFilters['toDate'][0], 'x')
          : null;

      // Reject future dates - set to undefined if date is in the future
      const toDate = toDateMoment && !toDateMoment.isAfter(moment()) ? toDateMoment.toDate() : undefined;

      this.toDate.update(toDate);
      needUpdate = true;
    }

    values = 'creationTimeSinceDate' in keyValueFilters ? keyValueFilters['creationTimeSinceDate'] : [];
    needUpdate = this.creationTimeSinceDate.updateByValuesIfNeeded(values) || needUpdate;

    values = 'type' in keyValueFilters ? keyValueFilters['type'] : [];
    needUpdate = this.typeFilters.updateByValuesIfNeeded(values) || needUpdate;

    values = 'integration' in keyValueFilters ? keyValueFilters['integration'] : [];
    needUpdate = this.integrationFilters.updateByValuesIfNeeded(values) || needUpdate;

    values = 'aiService' in keyValueFilters ? keyValueFilters['aiService'] : [];
    needUpdate = this.aiServiceFilters.updateByValuesIfNeeded(values) || needUpdate;

    values = 'clientApplication' in keyValueFilters ? keyValueFilters['clientApplication'] : [];
    needUpdate = this.clientApplicationFilters.updateByValuesIfNeeded(values) || needUpdate;

    values = 'actor' in keyValueFilters ? keyValueFilters['actor'] : [];
    if (values.length > 0 && !checkAllGivenIdsExistInOptions(this.actorFilters.getOptions$().value, values)) {
      this.actorFilters = new DynamicMultiSelectFilters([]);
      this._actorOnUpdate(values);
    }
    needUpdate = this.actorFilters.updateByValuesIfNeeded(values) || needUpdate;

    values = 'threadId' in keyValueFilters ? keyValueFilters['threadId'] : [];
    if (values.length > 0 && !checkAllGivenIdsExistInOptions(this.threadIdFilters.getOptions$().value, values)) {
      this.threadIdFilters = new DynamicMultiSelectFilters([]);
      this._threadIdOnUpdate(values);
    }
    needUpdate = this.threadIdFilters.updateByValuesIfNeeded(values) || needUpdate;

    values = 'agent' in keyValueFilters ? keyValueFilters['agent'] : [];
    if (values.length > 0 && !checkAllGivenIdsExistInOptions(this.agentFilters.getOptions$().value, values)) {
      this.agentFilters = new DynamicMultiSelectFilters([]);
      this._agentOnUpdate(values);
    }
    needUpdate = this.agentFilters.updateByValuesIfNeeded(values) || needUpdate;

    values = 'environment' in keyValueFilters ? keyValueFilters['environment'] : [];
    if (values.length > 0 && !checkAllGivenIdsExistInOptions(this.environmentFilters.getOptions$().value, values)) {
      this.environmentFilters = new DynamicMultiSelectFilters([]);
      this._environmentOnUpdate(values);
    }
    needUpdate = this.environmentFilters.updateByValuesIfNeeded(values) || needUpdate;

    values = 'chatMode' in keyValueFilters ? keyValueFilters['chatMode'] : [];
    needUpdate = this.chatModeFilters.updateByValuesIfNeeded(values) || needUpdate;

    values = 'toolOrigin' in keyValueFilters ? keyValueFilters['toolOrigin'] : [];
    if (values.length > 0 && !checkAllGivenIdsExistInOptions(this.toolOriginFilters.getOptions$().value, values)) {
      this.toolOriginFilters = new DynamicMultiSelectFilters([]);
      this._toolOriginOnUpdate(values);
    }
    needUpdate = this.toolOriginFilters.updateByValuesIfNeeded(values) || needUpdate;

    values = 'dataItem' in keyValueFilters ? keyValueFilters['dataItem'] : [];
    const dataItemIds = values.map((v) => v.split('|')[0]);
    if (values.length > 0 && !checkAllGivenIdsExistInOptions(this.dataItemFilters.getOptions$().value, dataItemIds)) {
      this.dataItemFilters = new DynamicMultiSelectFilters([]);
      this._dataItemOnUpdate(values);
    }
    needUpdate = this.dataItemFilters.updateByValuesIfNeeded(dataItemIds) || needUpdate;

    if (needUpdate) {
      if (updateQueryParams) {
        const filtersQuery = FiltersQueryTransformer.transformToQuery(this.getSelectedFilters());
        this._router.navigate([], {
          queryParams: { q: filtersQuery },
          queryParamsHandling: 'merge',
        });
      }
      this.onUpdate$.next(this.getSelectedFilters());
    }
  }

  public clearAllMultiSelectFiltersIfNeeded(): boolean {
    return (
      this.typeFilters.updateIfNeeded([]) ||
      this.actorFilters.updateIfNeeded([]) ||
      this.threadIdFilters.updateIfNeeded([]) ||
      this.integrationFilters.updateIfNeeded([]) ||
      this.aiServiceFilters.updateIfNeeded([]) ||
      this.clientApplicationFilters.updateIfNeeded([]) ||
      this.agentFilters.updateIfNeeded([]) ||
      this.environmentFilters.updateIfNeeded([]) ||
      this.chatModeFilters.updateIfNeeded([]) ||
      this.toolOriginFilters.updateIfNeeded([]) ||
      this.dataItemFilters.updateIfNeeded([])
    );
  }

  public getDynamicFilters(): string[] {
    return [
      FilterTypesEnum.Actor,
      FilterTypesEnum.ThreadId,
      FilterTypesEnum.AIAgentName,
      FilterTypesEnum.Environment,
      FilterTypesEnum.DataItem,
      FilterTypesEnum.ToolOrigin,
    ];
  }

  /**
   * Sets the toggle mode and updates Client Application and AI Service filter options accordingly.
   * This method is called when the dr_unified_pipeline FF is enabled and user switches between
   * Audit Log (option1) and Inline (option2) modes.
   *
   * @param toggle - 'option1' for Audit Log/AIDR mode, 'option2' for Inline mode
   * @param clearSelections - Whether to clear incompatible filter selections (default: true)
   */
  public setToggleMode(toggle: ToggleOption, clearSelections = true): void {
    if (!this.featureFlagsService.isFeatureEnabled(FeatureFlagTypes.UnifiedPipeline)) {
      return;
    }

    const modeChanged = this._currentToggleMode !== toggle;
    this._currentToggleMode = toggle;
    this._isUnifiedModeActive = true;

    if (toggle === 'option1') {
      const rsaDemo = this.featureFlagsService.isFeatureEnabled(FeatureFlagTypes.RsaDemoRuntimeBoundaries);
      this.clientApplicationFilters.updateOptions(aidrClientApplicationSelectOptions);
      this.aiServiceFilters.updateOptions(rsaDemo ? aidrAiServiceSelectOptionsWithRsaDemo : aidrAiServiceSelectOptions);
    } else {
      this.clientApplicationFilters.updateOptions(inlineUnifiedClientApplicationSelectOptions);
      this.aiServiceFilters.updateOptions(inlineUnifiedAiServiceSelectOptions);
    }

    if (modeChanged && clearSelections) {
      this.clientApplicationFilters.updateIfNeeded([]);
      this.aiServiceFilters.updateIfNeeded([]);
      if (toggle === 'option2') {
        this.chatModeFilters.updateIfNeeded([]);
      }
    }

    this._updateAllOptions();
  }

  /**
   * Returns the current toggle mode when unified pipeline is active.
   */
  public getCurrentToggleMode(): ToggleOption {
    return this._currentToggleMode;
  }

  /**
   * Returns whether unified mode is currently active.
   */
  public isUnifiedModeActive(): boolean {
    return this._isUnifiedModeActive;
  }

  private _initializeFilters() {
    this.fromDate = new DateFilter();
    this.toDate = new DateFilter();
    this.creationTimeSinceDate = new MultiSelectFilters(CreationTimeSinceOptions);
    // Initialize step type filters based on unified pipeline feature flag
    const isUnifiedPipelineEnabled = this.featureFlagsService.isFeatureEnabled(FeatureFlagTypes.UnifiedPipeline);
    this.typeFilters = new MultiSelectFilters(getStepTypeSelectOptions(isUnifiedPipelineEnabled));
    this.actorFilters = new DynamicMultiSelectFilters([]);
    this.threadIdFilters = new DynamicMultiSelectFilters([]);
    this.integrationFilters = new MultiSelectFilters([]);
    // AI Service and Client Application filters will be initialized dynamically based on active integrations
    this.aiServiceFilters = new MultiSelectFilters([]);
    this.clientApplicationFilters = new MultiSelectFilters([]);
    this.agentFilters = new DynamicMultiSelectFilters([]);
    this.environmentFilters = new DynamicMultiSelectFilters([]);
    // Chat Mode will be conditionally shown based on M365 presence
    this.chatModeFilters = new MultiSelectFilters(ChatModeSelectOptions);
    this.toolOriginFilters = new DynamicMultiSelectFilters([]);
    this.dataItemFilters = new DynamicMultiSelectFilters([]);
    this._updateAllOptions();
  }

  private _threadIdOnUpdate(threadIds: string[]) {
    const threadOptions = threadIds.map(
      (thread) =>
        ({
          id: thread,
          value: thread,
          displayName: thread,
          filterType: FilterTypesEnum.ThreadId,
          categoryName: FilterTypesEnum.ThreadId,
          withoutBeautifyDisplayName: true,
        }) as ISelectOption
    );

    const shouldUpdateSelectedOptions = this.threadIdFilters.updateOptions(threadOptions);
    this._updateAllOptions();
    if (shouldUpdateSelectedOptions) {
      this.onUpdate$.next(this.getSelectedFilters());
    }
  }

  private _dataItemOnUpdate(dataItemValues: string[]) {
    const dataItemOptions = dataItemValues.map((value) => {
      const [id, encodedName] = value.split('|');
      const displayName = encodedName ? decodeURIComponent(encodedName) : id;

      return {
        id: id,
        value: id, // Use ID for API filtering and option matching
        displayName: displayName,
        filterType: FilterTypesEnum.DataItem,
        categoryName: FilterTypesEnum.DataItem,
        withoutBeautifyDisplayName: true,
      } as ISelectOption;
    });

    const shouldUpdateSelectedOptions = this.dataItemFilters.updateOptions(dataItemOptions);
    this._updateAllOptions();
    if (shouldUpdateSelectedOptions) {
      this.onUpdate$.next(this.getSelectedFilters());
    }
  }

  private _actorOnUpdate(actorsIds: string[]) {
    this._fetchAndUpdateFilterOptions(actorsIds, 'mainActorDisplayName', FilterTypesEnum.Actor, this.actorFilters);
  }

  private _agentOnUpdate(agentsIds: string[]) {
    this._fetchAndUpdateFilterOptions(
      agentsIds,
      'aiAgentDisplayName',
      FilterTypesEnum.AIAgentName,
      this.agentFilters,
      (text) => this._displayCamelCaseStringPipe.transform(text)
    );
  }

  private _environmentOnUpdate(environmentsIds: string[]) {
    this._fetchAndUpdateFilterOptions(
      environmentsIds,
      'environment',
      FilterTypesEnum.Environment,
      this.environmentFilters
    );
  }

  private _toolOriginOnUpdate(toolOriginIds: string[]) {
    this._fetchAndUpdateFilterOptions(
      toolOriginIds,
      'toolOriginLabel',
      FilterTypesEnum.ToolOrigin,
      this.toolOriginFilters
    );
  }

  private _fetchAndUpdateFilterOptions(
    ids: string[],
    filterName: string,
    filterType: FilterTypesEnum,
    filters: DynamicMultiSelectFilters,
    transformDisplayName?: (text: string) => string
  ) {
    this._aiActivityHttpService
      .getDisplayNameById(filterName, ids)
      .subscribe((autocompleteResults: AutocompleteSearchResultsV2Model[]) => {
        const options: ISelectOption[] = autocompleteResults?.map((result) => {
          return {
            id: result.id,
            value: result.id,
            displayName: transformDisplayName ? transformDisplayName(result.resultText) : result.resultText,
            filterType: filterType,
            categoryName: filterType,
            withoutBeautifyDisplayName: true,
          } as ISelectOption;
        });

        const shouldUpdateSelectedOptions = filters.updateOptions(Object.values(options));

        this._updateAllOptions();
        if (shouldUpdateSelectedOptions) {
          this.onUpdate$.next(this.getSelectedFilters());
        }
      });
  }

  private _updateAllOptions() {
    this.allOptions$.next([
      ...this.typeFilters.getOptions$().value,
      ...this.actorFilters.getOptions$().value,
      ...this.threadIdFilters.getOptions$().value,
      ...this.integrationFilters.getOptions$().value,
      ...this.aiServiceFilters.getOptions$().value,
      ...this.clientApplicationFilters.getOptions$().value,
      ...this.agentFilters.getOptions$().value,
      ...this.environmentFilters.getOptions$().value,
      ...this.toolOriginFilters.getOptions$().value,
      ...this.dataItemFilters.getOptions$().value,
      // ChatMode excluded from main options - handled separately as static filter
    ]);
  }

  // Separate method to get all options including ChatMode for selection lookup
  public getAllOptionsIncludingChatMode(): ISelectOption[] {
    return [...this.allOptions$.value, ...this.chatModeFilters.getOptions$().value];
  }

  private _integrationsOnUpdate() {
    this._store.integrations$.subscribe((integrations: IntegrationModel[]) => {
      // Filter integrations based on user's access permissions for step viewing
      const filteredIntegrations = this._permissionsService.filterIntegrationsByPermission(
        integrations,
        PERMISSIONS.STEP_VIEWER
      );

      const integrationsPlatformSet: Set<string> = new Set();

      const integrationsOptionsMap: ISelectOption[] = filteredIntegrations.map((integration) => {
        integrationsPlatformSet.add(integration.platform);
        return fromIntegrationToSelectOption(integration);
      });

      const shouldUpdateSelectedOptions = this.integrationFilters.updateOptions(Object.values(integrationsOptionsMap));
      this._updateAllOptions();
      if (shouldUpdateSelectedOptions) {
        this.onUpdate$.next(this.getSelectedFilters());
      }
    });
  }

  /**
   * Dynamically updates AI Service and Client Application filter options based on active integrations
   * This ensures only relevant services and applications are shown to the user
   *
   * Note: When unified mode is active, these dynamic updates are skipped because
   * setToggleMode() manages the filter options based on toggle state instead.
   */
  private _updateDynamicFiltersFromIntegrations() {
    this._store.integrations$.subscribe((integrations) => {
      // Skip dynamic updates when unified mode is active - setToggleMode handles options
      if (this._isUnifiedModeActive) {
        return;
      }

      // Filter integrations based on user's access permissions for step viewing
      const filteredIntegrations = this._permissionsService.filterIntegrationsByPermission(
        integrations,
        PERMISSIONS.STEP_VIEWER
      );

      // Get active AI services from integrations (using Finding AI Services - same as Findings page)
      const activeAiServices = getActiveFindingAiServicesFromIntegrations(filteredIntegrations);

      // Create AI Service filter options only for active services
      const aiServiceOptions: ISelectOption[] = Array.from(activeAiServices)
        .map((service) => ({
          id: `${FilterTypesEnum.AiService}-${service}`,
          value: service,
          displayName: getAiServiceDisplayName(service),
          filterType: FilterTypesEnum.AiService,
          categoryName: FilterTypesEnum.AiService.toString(),
          iconPath: getAiServiceLogoPath(service),
          withoutBeautifyDisplayName: true,
        }))
        .sort((a, b) => a.displayName.localeCompare(b.displayName));

      // Update AI Service filter
      const aiServiceUpdated = this.aiServiceFilters.updateOptions(aiServiceOptions);
      this._updateAllOptions();
      if (aiServiceUpdated) {
        this.onUpdate$.next(this.getSelectedFilters());
      }
    });

    // Fetch available client applications from backend API
    this._updateClientApplicationFiltersFromApi();
  }

  /**
   * Dynamically updates Client Application filter options based on backend API
   * Uses the new v1-beta/detection/agentSteps/clientApplications endpoint
   *
   * Applies two-stage filtering:
   * 1. Gets list from backend API (based on customer's active integrations)
   * 2. Filters to only include items that exist in the frontend AgentClientApplication enum
   *
   * Note: When unified mode is active, these dynamic updates are skipped because
   * setToggleMode() manages the filter options based on toggle state instead.
   */
  private _updateClientApplicationFiltersFromApi() {
    this._aiActivityHttpService.getAvailableClientApplications().subscribe({
      next: (clientApplications: string[]) => {
        // Skip dynamic updates when unified mode is active - setToggleMode handles options
        if (this._isUnifiedModeActive) {
          return;
        }

        // Get valid client applications from frontend enum (whitelist)
        const validClientApps = new Set<string>(Object.values(AgentClientApplication));

        // Normalize, deduplicate, and filter backend response to only include apps in frontend enum
        // This removes items like "officecopilotsearchanswer", "outlooksidepane" that don't have icons
        const filteredClientApps = Array.from(
          new Set(
            clientApplications
              .filter((clientApp): clientApp is string => typeof clientApp === 'string')
              .map((clientApp) => clientApp.trim())
              .filter((clientApp) => clientApp && validClientApps.has(clientApp))
          )
        );

        // Create Client Application filter options only for valid apps
        const clientAppOptions: ISelectOption[] = filteredClientApps.sort().map((clientApp) => ({
          id: `${FilterTypesEnum.ClientApplication}-${clientApp}`,
          value: clientApp,
          displayName: getClientApplicationDisplayName(clientApp),
          categoryName: FilterTypesEnum.ClientApplication.toString(),
          filterType: FilterTypesEnum.ClientApplication,
          withoutBeautifyDisplayName: true,
          iconPath: getClientApplicationIconPath(clientApp),
        }));

        // Update filter options
        const clientAppUpdated = this.clientApplicationFilters.updateOptions(clientAppOptions);
        this._updateAllOptions();
        if (clientAppUpdated) {
          this.onUpdate$.next(this.getSelectedFilters());
        }
      },
      error: (error) => {
        console.error('Failed to fetch client applications:', error);
        // Fallback to empty list if API fails (only if not in unified mode)
        if (!this._isUnifiedModeActive) {
          this.clientApplicationFilters.updateOptions([]);
        }
      },
    });
  }
}
